import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Link } from '@mui/material';
import InputForm from './salaryCalculator/Inputs/SalaryCalculator/InputForm';
import TaxVisualization from './salaryCalculator/Visualisations/SalaryCalculator/TaxVisualisation';
import { calculateAll } from '../calculations/taxCalculations';
import OutputContainer from './salaryCalculator/Outputs/OutputContainer';
import { useConfig } from '../context/ConfigContext';
import { CalculatorSchema } from './StructuredData';
import { 
  makeMeta, 
  generateSalaryTitle, 
  generateSalaryDescription,
  makeWebApplicationJsonLd, 
  makeBreadcrumbJsonLd 
} from '../utils/seo';
import { getRelatedSalaryCalculators } from '../utils/calculatorRegistry';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const generateSeoText = (salary, results) => {
  const formattedSalary = formatCurrency(salary);
  const formattedTakeHome = formatCurrency(results.takeHome || 0);
  const formattedMonthly = formatCurrency((results.takeHome || 0) / 12);
  const formattedWeekly = formatCurrency((results.takeHome || 0) / 52);
  const formattedIncomeTax = formatCurrency(results.taxPaid || 0);
  const formattedNI = formatCurrency(results.nationalInsurance || 0);
  
  // Calculate total deductions in pounds
  const totalDeductions = (results.taxPaid || 0) + (results.nationalInsurance || 0) + (results.studentLoanRepayment || 0);
  const formattedTotalDeductions = formatCurrency(totalDeductions);

  return {
    summary: (
      <>
        With a salary of <strong style={{ color: '#34677A' }}>{formattedSalary}</strong> per year, your take-home pay would be <strong style={{ color: '#52ADD2' }}>{formattedTakeHome}</strong> annually, or <strong style={{ color: '#52ADD2' }}>{formattedMonthly}</strong> per month. This is after paying <strong style={{ color: '#87577D' }}>{formattedTotalDeductions}</strong> in taxes and deductions including income tax, National Insurance contributions, and any applicable student loan repayments.
      </>
    ),
    breakdown: (
      <>
        Your <strong style={{ color: '#34677A' }}>{formattedSalary}</strong> salary breaks down as follows: you'll pay <strong style={{ color: '#34677A' }}>{formattedIncomeTax}</strong> in income tax and <strong style={{ color: '#87577D' }}>{formattedNI}</strong> in National Insurance contributions. This leaves you with <strong style={{ color: '#52ADD2' }}>{formattedTakeHome}</strong> per year, which equals <strong style={{ color: '#52ADD2' }}>{formattedMonthly}</strong> per month or <strong style={{ color: '#52ADD2' }}>{formattedWeekly}</strong> per week in take-home pay.
      </>
    ),
    advice: (
      <>
        This calculation is based on the current UK tax rates for 2025-26 as published by{' '}
        <Link 
          href="https://www.gov.uk/income-tax-rates" 
          target="_blank" 
          rel="noopener noreferrer"
          sx={{ color: 'primary.main', textDecoration: 'underline' }}
        >
          HMRC
        </Link>
        . Your actual take-home pay may vary depending on your pension contributions, student loan repayments, and other deductions. Use our calculator above to adjust these settings for a more accurate estimate.
      </>
    )
  };
};

const SeoSalaryCalculator = ({ salary = 25000 }) => {
  const { config } = useConfig();

  const [userInput, setUserInput] = useState({
    salary: salary,
    overtime: { include: false, value: 0, inputType: 'amount' },
    pension: {
      active: false,
      contribution: 5,
      inputType: 'percentage',
      scheme: 'Auto-enrolment',
      calculationBasis: 'Qualifying Earnings',
      includeOvertime: false,
    },
    studentLoanPlan: { plan: "none" }
  });
  
  const [results, setResults] = useState(() => {
    return calculateAll(userInput, config);
  });

  const taxBands = config.TAX_BANDS;
  const maxSalary = config.MAX_SALARY;

  useEffect(() => {
    const calcResults = calculateAll(userInput, config);
    setResults(calcResults);
  }, [userInput, config]);

  useEffect(() => {
    setUserInput(prev => ({ ...prev, salary }));
  }, [salary]);

  const formattedSalary = formatCurrency(userInput.salary);
  const seoText = generateSeoText(userInput.salary, results);
  const relatedCalculators = getRelatedSalaryCalculators(userInput.salary, 4);
  
  // Enhanced SEO metadata and structured data
  const pageTitle = generateSalaryTitle(userInput.salary, results.takeHome);
  const pageDescription = generateSalaryDescription(userInput.salary, results.takeHome);
  const canonicalUrl = `https://salarytools.co.uk/${userInput.salary}-after-tax-uk`;
  
  const webAppJsonLd = makeWebApplicationJsonLd({
    name: `${formattedSalary} UK Salary Calculator`,
    url: canonicalUrl,
    description: `Calculate ${formattedSalary} UK salary breakdown with tax, National Insurance, and take-home pay`
  });
  
  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: "Home", url: "https://salarytools.co.uk" },
    { name: "Salary Calculator", url: "https://salarytools.co.uk/salary-calculator" },
    { name: `${formattedSalary} After Tax UK`, url: canonicalUrl }
  ]);

  return (
    <Container>
      {makeMeta({
        title: pageTitle,
        description: pageDescription,
        canonical: canonicalUrl,
        jsonLd: [webAppJsonLd, breadcrumbJsonLd]
      })}
      <CalculatorSchema 
        salary={userInput.salary}
        takeHomePay={results.takeHome}
        taxAmount={results.taxPaid}
        nationalInsuranceAmount={results.nationalInsurance}
      />

      <Box sx={{ pt: 8, mb: 6, textAlign: 'center' }}>
        <Typography variant="h1" component="h1" sx={{ 
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          fontWeight: 'bold',
          mb: 2
        }}>
          {formattedSalary} After Tax UK
        </Typography>
        <Typography variant="h5" component="h2" sx={{ 
          mb: 3,
          color: 'text.secondary',
          maxWidth: '800px',
          mx: 'auto',
          fontSize: '1.1rem'
        }}>
          {seoText.summary}
        </Typography>
        <Typography variant="body1" sx={{ 
          mb: 3,
          maxWidth: '600px',
          mx: 'auto',
          color: 'text.secondary'
        }}>
          {seoText.breakdown}
        </Typography>
        <Typography variant="body2" sx={{ 
          mb: 3, 
          fontStyle: 'italic',
          color: 'text.secondary',
          maxWidth: '600px',
          mx: 'auto'
        }}>
          {seoText.advice}
        </Typography>
        
        {/* Additional SEO content with HMRC links */}
        <Box sx={{ maxWidth: '800px', mx: 'auto', textAlign: 'left', mt: 6 }}>
          <Typography variant="h3" component="h2" sx={{ fontSize: '1.5rem', fontWeight: 'bold', mb: 3 }}>
            How UK Tax and National Insurance Works for {formattedSalary} Salaries
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            For the 2025-26 tax year, UK employees earning {formattedSalary} annually will pay income tax according to{' '}
            <Link 
              href="https://www.gov.uk/income-tax-rates/current-rates-and-allowances" 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', textDecoration: 'underline' }}
            >
              HMRC's current tax bands
            </Link>
            . Your personal allowance of £12,570 means the first £12,570 of your income is tax-free. Any income above this threshold is taxed at 20% (basic rate) up to £50,270, with higher rates applying to larger salaries.
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            National Insurance contributions are calculated separately using{' '}
            <Link 
              href="https://www.gov.uk/national-insurance-rates" 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', textDecoration: 'underline' }}
            >
              HMRC's National Insurance rates
            </Link>
            . For 2025-26, you'll pay 12% on earnings between £12,570 and £50,270, with a reduced rate of 2% on any earnings above £50,270. These contributions count towards your State Pension and other benefits.
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7 }}>
            The calculator above shows your exact breakdown based on current HMRC rates. You can adjust settings for pension contributions, student loan repayments, and other deductions to see how they affect your take-home pay. All calculations use the official thresholds and rates published by{' '}
            <Link 
              href="https://www.gov.uk/government/organisations/hm-revenue-customs" 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', textDecoration: 'underline' }}
            >
              HM Revenue and Customs
            </Link>
            .
          </Typography>
          
          {relatedCalculators.length > 0 && (
            <Box sx={{ mt: 5 }}>
              <Typography variant="h3" component="h2" sx={{ fontSize: '1.3rem', fontWeight: 'bold', mb: 3 }}>
                Related Salary Calculators
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {relatedCalculators.map((calc) => (
                  <Link 
                    key={calc.id}
                    href={calc.url}
                    sx={{ 
                      display: 'inline-block',
                      px: 3, 
                      py: 1.5, 
                      backgroundColor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      textDecoration: 'none',
                      color: 'primary.main',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    {calc.name}
                  </Link>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <InputForm onInputChange={setUserInput} config={config} initialSalary={salary} />

      <TaxVisualization
        bracketDetails={results.bracketDetails}
        maxSalary={maxSalary}
        taxBands={taxBands}
        salary={results.totalGrossIncome}
        taxableIncome={results.taxableIncome}
        taxPaid={results.taxPaid}
        takeHome={results.takeHome}
        effectiveTaxRate={results.effectiveTaxRate}
        nationalInsurance={results.nationalInsurance}
        darkMode={false}
      />
      <Box sx={{ mb: 8 }}>
        <OutputContainer results={results} salary={results.totalGrossIncome} config={config} />
      </Box>
    </Container>
  );
};

export default SeoSalaryCalculator;